// Face ID / Touch ID unlock — opt-in toggle stored in Preferences.
//
// Native binary requirement: @capgo/capacitor-native-biometric must be
// compiled into the iOS build (auto-registered via CocoaPods after
// `npx cap sync ios`). Info.plist must include NSFaceIDUsageDescription.

import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { Preferences } from "@capacitor/preferences";
import { isNative } from "./platform";

const PREF_KEY = "biometric_unlock_enabled";
const PROMPT_SEEN_KEY = "biometric_prompt_seen";

/** True once the user has been shown the Face ID intro screen (regardless of choice). */
export async function wasFaceIdAsked(): Promise<boolean> {
  const { value } = await Preferences.get({ key: PROMPT_SEEN_KEY });
  return value === "1";
}

export async function markFaceIdAsked(): Promise<void> {
  await Preferences.set({ key: PROMPT_SEEN_KEY, value: "1" });
}

export type BiometricStatus =
  | { kind: "web" }                                  // Not on native — feature hidden
  | { kind: "plugin_missing" }                       // Native call threw UNIMPLEMENTED — needs new TestFlight
  | { kind: "not_enrolled"; biometryType?: string }  // Plugin loaded, no Face/Touch ID set up on device
  | { kind: "available"; biometryType?: string }     // Ready to use
  | { kind: "errored"; message: string };            // Plugin loaded, isAvailable() rejected for another reason

/** Maps raw @capgo/capacitor-native-biometric biometryType values to readable labels. */
const BIO_TYPE_LABELS: Record<string, string> = {
  "0": "None",
  "1": "Touch ID",
  "2": "Face ID",
  "3": "Fingerprint",
  "4": "Face Authentication",
  "5": "Iris Authentication",
  "6": "Multiple",
};

export function biometryTypeLabel(biometryType?: string): string | undefined {
  if (!biometryType) return undefined;
  return BIO_TYPE_LABELS[biometryType] ?? biometryType;
}

const UNIMPLEMENTED_RE = /UNIMPLEMENTED|not implemented|is not a function|no such method/i;

/**
 * Rich status for UI — call-based, no registry short-circuit.
 * `Capacitor.isPluginAvailable("NativeBiometric")` returns false even when
 * the pod works fine, so we trust the actual call instead.
 */
export async function getBiometricStatus(): Promise<BiometricStatus> {
  if (!isNative()) return { kind: "web" };
  try {
    const res: any = await NativeBiometric.isAvailable();
    const biometryType = res?.biometryType != null ? String(res.biometryType) : undefined;
    if (res?.isAvailable) return { kind: "available", biometryType };
    return { kind: "not_enrolled", biometryType };
  } catch (e: any) {
    const msg = e?.message ?? String(e);
    if (UNIMPLEMENTED_RE.test(msg)) return { kind: "plugin_missing" };
    return { kind: "errored", message: msg };
  }
}

/** True only on native iOS/Android with a usable biometric sensor enrolled. */
export async function isBiometricAvailable(): Promise<boolean> {
  const s = await getBiometricStatus();
  return s.kind === "available";
}

/** Has the user opted in to require Face ID on launch? */
export async function isBiometricUnlockEnabled(): Promise<boolean> {
  const { value } = await Preferences.get({ key: PREF_KEY });
  return value === "1";
}

export async function setBiometricUnlockEnabled(on: boolean): Promise<void> {
  if (on) await Preferences.set({ key: PREF_KEY, value: "1" });
  else await Preferences.remove({ key: PREF_KEY });
}

/** Prompt for Face ID. Throws on user cancel / failure. */
export async function verifyBiometric(reason = "Unlock Roava"): Promise<void> {
  if (!isNative()) throw new Error("Biometric unlock is only available on the iOS app.");
  await NativeBiometric.verifyIdentity({
    reason,
    title: "Unlock Roava",
    subtitle: "Use Face ID to continue",
    description: reason,
    useFallback: true, // allow device passcode fallback
  });
}

// In-memory + sessionStorage-backed flag so a WKWebView content-process
// restart (OOM from a large image, etc.) does NOT re-prompt Face ID within
// the same app launch. sessionStorage survives content-process restarts but
// is cleared on full app kill, which is exactly the behavior we want.
const UNLOCK_KEY = "roava:unlocked-at";
const UNLOCK_TTL_MS = 10 * 60 * 1000; // 10 minutes
let unlockedThisLaunch = false;

function readStoredUnlock(): boolean {
  try {
    if (typeof sessionStorage === "undefined") return false;
    const raw = sessionStorage.getItem(UNLOCK_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < UNLOCK_TTL_MS;
  } catch {
    return false;
  }
}

export function markUnlocked() {
  unlockedThisLaunch = true;
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(UNLOCK_KEY, String(Date.now()));
    }
  } catch { /* ignore quota */ }
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("roava:biometric-unlocked"));
    }
  } catch { /* ignore */ }
}
export function isUnlockedThisLaunch() {
  if (unlockedThisLaunch) return true;
  if (readStoredUnlock()) {
    // Hydrate in-memory flag so subsequent calls are cheap.
    unlockedThisLaunch = true;
    return true;
  }
  return false;
}
export function resetUnlock() {
  unlockedThisLaunch = false;
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(UNLOCK_KEY);
    }
  } catch { /* ignore */ }
}


